import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { createUserSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useBuildingsShortQuery,
  useCreateUserMutation,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  useProvidersShortQuery,
  useRolesQuery,
  useTeamsShortQuery,
  useTilesQuery,
  useUserListShortQuery,
} from '@common/cache';
import { paths, USER_SUFFIX } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { ROOT_ORGANIZATION_ID } from '@common/types/constants.ts';
import { InferType } from 'yup';

const suffixOptions = [
  'APP',
  'ARNP',
  'CEMT',
  'CMA',
  'CNA',
  'CRT',
  'DO',
  'DNP',
  'EMT',
  'FNP',
  'LCSW',
  'MA',
  'MD',
  'NP',
  'PA',
  'PA-C',
];

type IFormShape = InferType<typeof createUserSchema>;

const initialValues: IFormShape = {
  email: '',
  firstName: '',
  lastName: '',
  suffix: '',
  role: 'care_manager',
  recordId: '',
  teamId: '',
  supervisor_id: '',
};

export const useCreateUser = (handleOpenUserDetails: (id: string) => void) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
    resetField,
    getValues,
  } = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(createUserSchema),
    defaultValues: initialValues,
  });
  const { mutateAsync, isPending } = useCreateUserMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [roleName, setRoleName] = useState('');
  const { data: roles = [] } = useRolesQuery();
  const { data: titles = [] } = useTilesQuery();
  const { data: organizationsShortOptions = [] } = useOrganizationsShortQuery();
  const { data: partnersShortOptions = [] } = usePartnerShortQuery();
  const { data: buildingsShortOptions = [] } = useBuildingsShortQuery({});
  const { data: providersShortOptions = [] } = useProvidersShortQuery({});
  const { data: teamsShortOptions = [] } = useTeamsShortQuery({});
  const { data: supervisorOptions = [] } = useUserListShortQuery({
    roles: ['organization_supervisor', 'partner_supervisor'],
  });

  const handleClose = () => {
    if (isDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const selectedRoleName = useWatch({ control, name: 'role' });
  const selectedRole = roles?.find(item => item.role_name === selectedRoleName);

  useEffect(() => {
    if (selectedRoleName) {
      const selectedRole = roles?.find(
        item => item.role_name === selectedRoleName,
      );

      setRoleName(selectedRole?.role_name || '');
      resetField('teamId');
      resetField('recordId');
    }
  }, [selectedRoleName]);

  const dynamicEntityNameOptions = () => {
    switch (selectedRole?.record_type) {
      case 'root':
        return [{ id: ROOT_ORGANIZATION_ID, name: 'All Organizations' }];
      case 'organization':
        return organizationsShortOptions;
      case 'partner':
        return partnersShortOptions;
      case 'building':
        return buildingsShortOptions;
      case 'provider':
        return providersShortOptions;
      default:
        return [{ id: '', name: '' }];
    }
  };

  const showTeamDropdown =
    !roleName ||
    roleName === 'care_navigator' ||
    roleName === 'care_manager' ||
    roleName === 'wellness_coordinator' ||
    roleName === 'operations_coordinator';

  const submitHandler = async (values: IFormShape) => {
    try {
      const response = await mutateAsync({
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        suffix: (values.suffix as USER_SUFFIX) || null,
        record_id: values.recordId,
        role: values.role,
        title:
          values.title as paths[`/user`]['post']['requestBody']['content']['application/json']['title'],
        ...(!!values.teamId && { team_id: values.teamId }),
        ...(!!values.supervisor_id && { supervisor_id: values.supervisor_id }),
      });

      if (response.data.id) {
        handleOpenUserDetails(response.data.id);
      }

      setIsOpen(false);
      reset();
      toast.success(
        'You added a new user! We’ve sent them an email to create a password and activate their account.',
      );
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        suffix: (values.suffix as USER_SUFFIX) || null,
        record_id: values.recordId,
        role: values.role,
        title:
          values.title as paths[`/user`]['post']['requestBody']['content']['application/json']['title'],
        ...(!!values.teamId && { team_id: values.teamId }),
        ...(!!values.supervisor_id && { supervisor_id: values.supervisor_id }),
      });

      toast.success(
        'You added a new user! We’ve sent them an email to create a password and activate their account.',
      );
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    control,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    roles: roles.map(o => ({
      value: o.role_name,
      label: o.display_name,
    })),
    titles: titles.map(o => ({
      value: o,
      label: o,
    })),
    recordType: selectedRole?.record_type,
    dynamicEntityNameOptions,
    showTeamDropdown,
    teamsShortOptions: teamsShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    suffixOptions: suffixOptions.map(o => ({
      value: o,
      label: o,
    })),
    supervisorOptions: supervisorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
